import { hasModifierKey } from '@angular/cdk/keycodes'
import {
  ConnectedPosition,
  OverlayRef,
  createFlexibleConnectedPositionStrategy,
  createOverlayRef,
  createRepositionScrollStrategy,
} from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import { DOCUMENT } from '@angular/common'
import {
  DestroyRef,
  Directive,
  ElementRef,
  Injector,
  ViewContainerRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { Subscription, filter, merge } from 'rxjs'
import { DateTimePickerPanelComponent } from './date-time-picker-panel/date-time-picker-panel.component'
import { PickerMode } from './date-time-picker.types'

const GAP = 4

/**
 * Below the trigger, start-aligned; flips above near the viewport bottom, then end-aligned near its right edge. When
 * neither fits (the 485px date-time panel in a short window), the overlay is pushed into the viewport, over the trigger,
 * rather than cut off.
 */
const POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: GAP },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -GAP },
  { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: GAP },
  { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -GAP },
]

/**
 * Opens `seiri-date-time-picker-panel` in an overlay from any trigger: `<button seiriDateTimePicker="date"
 * [pickerValue]="from" (picked)="setFrom($event)">`. The attribute's value is the mode, `datetime` when empty.
 *
 * Zone-free, like the panel: `pickerValue` and `picked` are browser-local wall-clock `Date`s. `picked` emits a new
 * `Date`, never `undefined`, and the panel closes after it.
 *
 * The panel closes on a click outside it (a transparent backdrop, as MatMenu and MatDatepicker do), on Escape and
 * after a pick, and emits `closed` each time. Focus moves into the panel on open, and on close back to what had it (the trigger, or a field's input).
 */
@Directive({
  selector: '[seiriDateTimePicker]',
  exportAs: 'seiriDateTimePicker',
  host: {
    '(click)': 'open()',
    'aria-haspopup': 'dialog',
    '[attr.aria-expanded]': 'isOpen()',
  },
})
export class DateTimePickerDirective {
  readonly mode = input<PickerMode, PickerMode | ''>('datetime', {
    alias: 'seiriDateTimePicker',
    transform: (mode) => mode || 'datetime',
  })
  readonly pickerValue = input<Date | null | undefined>(null)
  readonly pickerMin = input<Date | null | undefined>(null)
  readonly pickerMax = input<Date | null | undefined>(null)
  readonly pickerStartAt = input<Date | null | undefined>(null)
  readonly pickerStartView = input<'days' | 'years'>('days')
  /** What the panel lines up with; the trigger itself by default. A form field passes its outline. */
  readonly pickerOrigin = input<ElementRef<HTMLElement> | HTMLElement | null>(null)
  readonly picked = output<Date>()
  readonly closed = output<void>()

  readonly isOpen = signal(false)

  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly injector = inject(Injector)
  private readonly viewContainer = inject(ViewContainerRef)
  private readonly document = inject(DOCUMENT)
  private overlayRef: OverlayRef | null = null
  private subscriptions = new Subscription()
  /** Where focus goes back on close: what had it on open (a field's input), else the trigger. */
  private returnFocusTo: HTMLElement | null = null

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.subscriptions.unsubscribe()
      this.overlayRef?.dispose()
    })
  }

  open(): void {
    if (this.isOpen()) return
    const focused = this.document.activeElement
    this.returnFocusTo = focused instanceof HTMLElement && focused !== this.document.body ? focused : null
    const overlayRef = this.createOverlay()
    const panel = overlayRef.attach(new ComponentPortal(DateTimePickerPanelComponent, this.viewContainer))
    panel.setInput('mode', this.mode())
    panel.setInput('value', this.pickerValue())
    panel.setInput('min', this.pickerMin())
    panel.setInput('max', this.pickerMax())
    panel.setInput('startAt', this.pickerStartAt())
    panel.setInput('startView', this.pickerStartView())
    this.isOpen.set(true)

    this.subscriptions = new Subscription()
    this.subscriptions.add(
      panel.instance.picked.subscribe((date) => {
        this.picked.emit(date)
        this.close()
      }),
    )
    this.subscriptions.add(
      merge(
        overlayRef.backdropClick(),
        overlayRef.keydownEvents().pipe(filter((event) => event.key === 'Escape' && !hasModifierKey(event))),
      ).subscribe((event) => {
        event.preventDefault()
        this.close()
      }),
    )
  }

  close(): void {
    if (!this.isOpen()) return
    this.subscriptions.unsubscribe()
    this.overlayRef?.detach()
    this.isOpen.set(false)
    const returnTo = this.returnFocusTo?.isConnected ? this.returnFocusTo : this.element.nativeElement
    returnTo.focus()
    this.returnFocusTo = null
    this.closed.emit()
  }

  private createOverlay(): OverlayRef {
    if (this.overlayRef) return this.overlayRef
    const origin = this.pickerOrigin() ?? this.element
    this.overlayRef = createOverlayRef(this.injector, {
      positionStrategy: createFlexibleConnectedPositionStrategy(this.injector, origin)
        .withPositions(POSITIONS)
        .withFlexibleDimensions(false)
        .withViewportMargin(GAP * 2),
      scrollStrategy: createRepositionScrollStrategy(this.injector),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    })
    return this.overlayRef
  }
}
